import React from 'react';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, ListSubheader, Divider, FormHelperText, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Controller } from 'react-hook-form';
import { FormSelectInputProps, FormSelectOption, FormInputProps } from './FormInputProps';
import { allCountriesSorted, allLanguages } from 'languages';
import i18next from 'i18next';
import { useWatch } from 'react-hook-form';
import { getSubdivisionsForCountry, getSubdivisionName } from 'semshared/utils/subdivisions';

/**
 * Search input field for filtering dropdown options.
 * Prevents menu close on click/keydown events.
 */
export const FilterTextField = ({
  filterValue,
  setFilterValue,
  filterRef,
  placeholder = i18next.t('common-search-placeholder') as string,
}: {
  filterValue: string;
  setFilterValue: (value: string) => void;
  filterRef: React.RefObject<HTMLInputElement>;
  placeholder?: string;
}) => {
  return (
    <MenuItem
      sx={{
        p: 1.5,
        pb: 1,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
      onClickCapture={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={e => e.stopPropagation()}
    >
      <TextField
        inputRef={filterRef}
        placeholder={placeholder}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'action.hover',
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
            },
            '&:hover': {
              backgroundColor: 'action.selected',
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
            },
          },
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        fullWidth
        value={filterValue}
        onChange={e => {
          e.preventDefault();
          e.stopPropagation();
          setFilterValue(e.target.value);
          setTimeout(() => filterRef.current?.focus(), 10);
        }}
      />
    </MenuItem>
  );
};

/**
 * Builds MenuItem components from option array.
 * Handles dividers (ListSubheader) and disabled states.
 * When selectedValues is provided, renders a checkbox before each option label.
 */
export const buildMenuItemsFromOptions = (options: FormSelectOption[], onSelect?: (value?: any) => any, selectedValues?: any[]) => {
  return options.map((option, index) => {
    if (option.divider) {
      return <ListSubheader key={index}>{option.label || <Divider />}</ListSubheader>;
    } else {
      const isChecked = selectedValues !== undefined && selectedValues.includes(option.value);
      return (
        <MenuItem
          key={index}
          disabled={!!option.disabled}
          value={option.value}
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            mx: 1,
            mb: 0.5,
            transition: 'all 0.15s ease-in-out',
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': {
              backgroundColor: theme => `${theme.palette.primary.main}15`,
              '&:hover': {
                backgroundColor: theme => `${theme.palette.primary.main}25`,
              },
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
          {...(!option.disabled && onSelect ? { onClick: () => onSelect(option.value) } : {})}
        >
          {selectedValues !== undefined ? (
            <>
              <Checkbox checked={isChecked} size="small" sx={{ p: 0.5, mr: 0.5 }} />
              <ListItemText primary={option.label} />
            </>
          ) : (
            option.label
          )}
        </MenuItem>
      );
    }
  });
};

/**
 * Dropdown select component with optional search filter.
 * Uses filterText for display if available, otherwise extracts text from label.
 * Supports complex value types (objects) via JSON comparison.
 */
export const FormInputDropdown = ({ name, control, label, disabled, options, helperText, onSelected, enableFilter, filterPlaceholder }: FormSelectInputProps) => {
  const [filterValue, setFilterValue] = React.useState('');
  const filterRef = React.useRef<HTMLInputElement>(null);

  return (
    <FormControl fullWidth>
      <InputLabel id={`${name}-label`} disabled={disabled}>
        {label}
      </InputLabel>
      <Controller
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select
              fullWidth
              label={label}
              {...(typeof disabled !== 'undefined' ? { disabled } : {})}
              labelId={`${name}-label`}
              onChange={(...args) => {
                const selected = (args[0] as any)?.target?.value;
                const mapped = selected === '' ? null : selected;
                onChange(mapped);
                if (onSelected) {
                  onSelected(mapped);
                }
              }}
              error={!!error}
              value={value ?? ''}
              sx={{
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              renderValue={selected => {
                const selectedOption = options.find(o => {
                  if (typeof selected === 'object' && selected !== null && typeof o.value === 'object' && o.value !== null) {
                    return JSON.stringify(o.value) === JSON.stringify(selected);
                  }
                  return o.value === selected;
                });
                if (selectedOption) {
                  // Use filterText if available, otherwise extract text from label
                  if (selectedOption.filterText) {
                    return selectedOption.filterText;
                  }
                  // If label is a string, use it directly
                  if (typeof selectedOption.label === 'string') {
                    return selectedOption.label;
                  }
                  // For complex React elements, we can't easily extract text, so return empty
                  return '';
                }
                return '';
              }}
            >
              {enableFilter && <FilterTextField filterValue={filterValue} setFilterValue={setFilterValue} filterRef={filterRef} placeholder={filterPlaceholder} />}
              {buildMenuItemsFromOptions(
                options.filter(o => {
                  if (o.divider) return true;
                  const searchText = (o.filterText || o.label)?.toString().toLowerCase() || '';
                  return searchText.includes(filterValue.toLowerCase());
                }),
              )}
            </Select>
            <FormHelperText error={!!error}>{error ? error.message : helperText ? helperText : ' '}</FormHelperText>
          </>
        )}
        control={control}
        name={name}
      />
    </FormControl>
  );
};

interface SimpleDropdownProps {
  value: any;
  name: string;
  label: string;
  disabled?: boolean;
  options: FormSelectOption[];
  helperText?: string;
  enableFilter?: boolean;
  filterPlaceholder?: string;
  onChange: (value: any) => void;
  /** Enable multi-select mode; value becomes an array */
  multiple?: boolean;
}

/**
 * Standalone dropdown without react-hook-form integration.
 * Useful for non-form contexts or manual state management.
 * Supports multiple selection via the `multiple` prop.
 */
export const SimpleDropdown = ({ value, name, label, disabled, options, helperText, onChange, enableFilter, filterPlaceholder, multiple }: SimpleDropdownProps) => {
  const [filterValue, setFilterValue] = React.useState('');
  const filterRef = React.useRef<HTMLInputElement>(null);

  const normalizedValue = multiple ? (Array.isArray(value) ? value : []) : (value ?? '');

  const renderSelectedValue = (selected: any) => {
    if (multiple && Array.isArray(selected)) {
      const labels = (selected as any[])
        .map(s => options.find(o => o.value === s)?.label ?? String(s))
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : '';
    }
    const selectedOption = options.find(o => {
      if (typeof selected === 'object' && selected !== null && typeof o.value === 'object' && o.value !== null) {
        return JSON.stringify(o.value) === JSON.stringify(selected);
      }
      return o.value === selected;
    });
    return selectedOption ? String(selectedOption.label ?? '') : '';
  };

  return (
    <FormControl fullWidth>
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <Select
        fullWidth
        label={label}
        multiple={multiple}
        {...(typeof disabled !== 'undefined' ? { disabled } : {})}
        labelId={`${name}-label`}
        onChange={e => {
          const val = (e as any)?.target?.value;
          if (multiple) {
            onChange(Array.isArray(val) ? val : []);
          } else {
            onChange(val === '' ? null : val);
          }
        }}
        value={normalizedValue}
        sx={{
          '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        renderValue={renderSelectedValue}
      >
        {enableFilter && <FilterTextField filterValue={filterValue} setFilterValue={setFilterValue} filterRef={filterRef} placeholder={filterPlaceholder} />}
        {buildMenuItemsFromOptions(
          options.filter(o => {
            if (o.divider) return true;
            const searchText = (o.filterText || o.label)?.toString().toLowerCase() || '';
            return searchText.includes(filterValue.toLowerCase());
          }),
          undefined,
          multiple ? normalizedValue : undefined,
        )}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
};

/**
 * Pre-configured dropdown for country selection.
 * Uses allCountriesSorted with AT, DE, CH prioritized.
 */
export const FormInputCountryDropdown = (props: Omit<FormSelectInputProps, 'options'>) => {
  return (
    <FormInputDropdown
      options={[
        ...(!props.required
          ? [
              {
                label: <em>{i18next.t('generic-selection-empty')}</em>,
                value: null,
              },
            ]
          : []),
        ...allCountriesSorted.map(c => (c ? { value: c.code, label: c.label } : ({ divider: true } as any))),
      ]}
      {...props}
    />
  );
};

/**
 * Pre-configured dropdown for language selection.
 * Uses allLanguages with German display names.
 */
export const FormInputLanguageDropdown = (props: Omit<FormSelectInputProps, 'options'>) => {
  return (
    <FormInputDropdown
      options={[
        ...(!props.required
          ? [
              {
                label: <em>{i18next.t('generic-selection-empty')}</em>,
                value: null,
              },
            ]
          : []),
        ...allLanguages.map(l => (l ? { value: l.code, label: l.label } : ({ divider: true } as any))),
      ]}
      {...props}
    />
  );
};

interface FormInputSubdivisionDropdownProps extends Omit<FormInputProps, 'options'> {
  countryFieldName: string;
  onSelected?: (value?: any) => void;
}

/**
 * Dropdown for state/province selection based on selected country.
 * Watches countryFieldName and updates options dynamically.
 * Enables filter automatically when more than 10 subdivisions available.
 */
export const FormInputSubdivisionDropdown = ({ countryFieldName, ...props }: FormInputSubdivisionDropdownProps) => {
  const countryCode = useWatch({ control: props.control, name: countryFieldName });
  const subdivisions = React.useMemo(() => getSubdivisionsForCountry(countryCode || ''), [countryCode]);

  const options: FormSelectOption[] = React.useMemo(
    () => [
      {
        label: <em>{i18next.t('generic-selection-empty')}</em>,
        value: null,
      },
      ...subdivisions.map(s => ({
        value: s.code,
        label: s.name,
        filterText: s.name,
      })),
    ],
    [subdivisions],
  );

  return (
    <FormInputDropdown
      options={options}
      enableFilter={subdivisions.length > 10}
      filterPlaceholder={String(i18next.t('common-search-placeholder'))}
      {...props}
    />
  );
};

export { getSubdivisionName };
